import { Box, Button, Chip, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import type { BankLine } from '../../data/types';
import { distributionTotals } from '../../domain/distributionCalc';
import { date, money } from '../../lib/format';
import { useRegistry } from '../../store/RegistryStore';
import { sumBy, useTenantScope } from '../../store/selectors';
import DataTable from '../../ui/DataTable';
import { useToast } from '../../ui/feedback';
import { AboutThisPage, PageHeader, StatCard, StatGrid, StatusChip } from '../../ui/primitives';
import MatchDialog from './MatchDialog';
import PaymentExceptionsTab from './PaymentExceptionsTab';

export default function PaymentsPage() {
  const scope = useTenantScope();
  const { dispatch } = useRegistry();
  const toast = useToast();
  const [tab, setTab] = useState(0);
  const [matching, setMatching] = useState<BankLine | null>(null);
  const [showDone, setShowDone] = useState(false);
  const lines = useMemo(
    () =>
      scope.bankLines.filter(
        (line) => showDone || line.status === 'UNMATCHED' || line.status === 'SUGGESTED',
      ),
    [scope.bankLines, showDone],
  );
  const open = scope.bankLines.filter(
    (line) => line.status === 'UNMATCHED' || line.status === 'SUGGESTED',
  );
  const outgoing = scope.distributions.filter((item) => item.abaFileName);

  const suggestion = (line: BankLine) => {
    if (line.suggestedCallId && line.suggestedHolderId) {
      const call = scope.calls.find((item) => item.id === line.suggestedCallId);
      return `${scope.holderById.get(line.suggestedHolderId)?.name} · ${scope.fundById.get(call?.fundId ?? '')?.code} Call ${call?.number}`;
    }
    if (line.suggestedApplicationId) {
      const application = scope.applications.find(
        (item) => item.id === line.suggestedApplicationId,
      );
      return `${application?.reference} · ${application?.applicantName}`;
    }
    return '—';
  };

  return (
    <>
      <PageHeader
        title="Payments & bank"
        subtitle="Money in from the bank feed, matched to calls and applications; money out as ABA files."
      />
      <AboutThisPage>
        <p>
          Each fund&apos;s applications account is connected to a daily bank feed. Receipts are
          matched by BPAY reference or application reference first, then by amount and payer name.
          Suggested matches need one click; anything else is matched manually or excluded (for
          example, bank interest).
        </p>
      </AboutThisPage>
      <StatGrid>
        <StatCard
          label="To match"
          value={open.length}
          tone={open.length ? 'warning' : 'neutral'}
          hint={money(sumBy(open, (line) => line.amount))}
        />
        <StatCard
          label="Suggested matches"
          value={open.filter((line) => line.status === 'SUGGESTED').length}
          tone="info"
        />
        <StatCard
          label="Matched this month"
          value={scope.bankLines.filter((line) => line.status === 'MATCHED').length}
          tone="success"
        />
        <StatCard label="ABA files generated" value={outgoing.length} hint="Distributions" />
      </StatGrid>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={(_event, next) => setTab(next)}>
          <Tab label="Money in (bank feed)" />
          <Tab label="Money out (ABA files)" />
          <Tab
            label={`Exceptions (${scope.paymentExceptions.filter((item) => item.status === 'OPEN' || item.status === 'AWAITING_INVESTOR').length})`}
          />
        </Tabs>
      </Box>
      {tab === 0 && (
        <Paper variant="outlined">
          <Stack direction="row" spacing={1} sx={{ p: 2 }}>
            <Chip
              label="Open"
              color={!showDone ? 'primary' : 'default'}
              variant={!showDone ? 'filled' : 'outlined'}
              onClick={() => setShowDone(false)}
            />
            <Chip
              label="All lines"
              color={showDone ? 'primary' : 'default'}
              variant={showDone ? 'filled' : 'outlined'}
              onClick={() => setShowDone(true)}
            />
          </Stack>
          <DataTable
            rows={lines}
            rowKey={(row) => row.id}
            dense
            defaultSort={{ key: 'date', direction: 'desc' }}
            emptyMessage="All bank lines are matched"
            columns={[
              {
                key: 'date',
                label: 'Date',
                sortValue: (row) => row.date,
                render: (row) => date(row.date),
              },
              {
                key: 'fund',
                label: 'Account',
                render: (row) => scope.fundById.get(row.fundId)?.code,
              },
              {
                key: 'desc',
                label: 'Description',
                render: (row) => (
                  <>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {row.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Ref {row.reference}
                    </Typography>
                  </>
                ),
              },
              {
                key: 'amount',
                label: 'Amount',
                align: 'right',
                sortValue: (row) => row.amount,
                render: (row) => money(row.amount, true),
              },
              {
                key: 'suggest',
                label: 'Suggested match',
                render: (row) =>
                  row.status === 'SUGGESTED'
                    ? suggestion(row)
                    : row.status === 'MATCHED'
                      ? `Matched by ${row.matchedBy}`
                      : '—',
              },
              {
                key: 'status',
                label: 'Status',
                render: (row) => <StatusChip status={row.status} />,
              },
              {
                key: 'action',
                label: '',
                align: 'right',
                render: (row) => {
                  if (row.status === 'SUGGESTED') {
                    return (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={(event) => {
                          event.stopPropagation();
                          dispatch({
                            type: 'BANK_LINE_MATCH',
                            lineId: row.id,
                            callId: row.suggestedCallId,
                            holderId: row.suggestedHolderId,
                            applicationId: row.suggestedApplicationId,
                          });
                          toast(
                            row.suggestedCallId
                              ? 'Matched: call payment posted to the ledger'
                              : 'Matched: application monies received',
                          );
                        }}
                      >
                        Accept
                      </Button>
                    );
                  }
                  if (row.status === 'UNMATCHED') {
                    return (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={(event) => {
                          event.stopPropagation();
                          setMatching(row);
                        }}
                      >
                        Match…
                      </Button>
                    );
                  }
                  return null;
                },
              },
            ]}
          />
        </Paper>
      )}
      {tab === 2 && <PaymentExceptionsTab />}
      {tab === 1 && (
        <Paper variant="outlined">
          <DataTable
            rows={outgoing}
            rowKey={(row) => row.id}
            dense
            defaultSort={{ key: 'date', direction: 'desc' }}
            columns={[
              {
                key: 'file',
                label: 'File',
                render: (row) => (
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {row.abaFileName}
                  </Typography>
                ),
              },
              { key: 'fund', label: 'Fund', render: (row) => scope.fundById.get(row.fundId)?.code },
              {
                key: 'date',
                label: 'Payment date',
                sortValue: (row) => row.paymentDate,
                render: (row) => date(row.paymentDate),
              },
              {
                key: 'records',
                label: 'Payments',
                align: 'right',
                render: (row) => row.lines.filter((line) => line.method === 'CASH').length,
              },
              {
                key: 'total',
                label: 'Total',
                align: 'right',
                render: (row) => money(distributionTotals(row).cash, true),
              },
              {
                key: 'status',
                label: 'Status',
                render: (row) => (
                  <StatusChip
                    status={row.status === 'PAID' ? 'COMPLETED' : 'PENDING'}
                    label={row.status === 'PAID' ? 'Released' : 'Ready to upload'}
                  />
                ),
              },
            ]}
          />
        </Paper>
      )}
      <MatchDialog line={matching} onClose={() => setMatching(null)} />
    </>
  );
}
